import { Component, OnInit, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ApiService,
  SessionService,
  CartStateService,
  CHECKOUT_PROFILE_PROVIDER,
} from '../../services';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import {
  CartItem,
  EventModel,
  initiatePaymentReq,
  Order,
  AddToCartRequest,
  Address,
  DeliveryLocation,
} from '../../models';
import { FilesService } from '@hypenation/shared';
import { AuthStateService } from '@hypenation/auth';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { PaymentMethodSelectionComponent } from '../payment-method-selection/payment-method-selection.component';

import { OrderFee } from '../../models/order-fees';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  CountryISO,
  SearchCountryField,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import { switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'hypenation-tickets-shopping-cart',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NzImageModule,
    NzNotificationModule,
    NzInputModule,
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzCardModule,
    NzButtonModule,
    NzSwitchModule,
    NzSelectModule,
    PaymentMethodSelectionComponent,
    NzSkeletonModule,
    NzProgressModule,
    NzStepsModule,
    NgxIntlTelInputModule,
  ],
  templateUrl: './shopping-cart.component.html',
  styleUrl: './shopping-cart.component.css',
  providers: [NzModalService],
})
export class ShoppingCartComponent implements OnInit {
  cartItems: CartItem[] = [];
  fees: OrderFee[] = [];
  loadingCart = false;
  loadingFees = false;
  loadingOrderEvent = false;
  checkoutForm: FormGroup;
  checkoutLoading = false;
  objectKeys = Object.keys;
  loadingClearCart = false;

  /** True when the cart's event offers physical delivery (gates the UI). */
  deliveryAvailable = false;
  /** Cart currency, used to filter delivery locations to a matching currency. */
  cartCurrency = '';
  /** ACTIVE, currency-matched delivery locations offered to the customer. */
  deliveryLocations: DeliveryLocation[] = [];
  loadingDeliveryLocations = false;
  /** True when delivery was enabled but no eligible location exists. */
  deliveryUnavailable = false;
  selectedDeliveryLocation: DeliveryLocation | null = null;
  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  PhoneNumberFormat = PhoneNumberFormat;

  /**
   * Resolved image URL keyed by parent event UID. Cart items don't carry
   * an image directly, so we look up the parent event and resolve its
   * imageFileName via FilesService (which now caches the resolved URL).
   */
  imageUrls = new Map<string, string>();

  /**
   * For a seat held in this cart: the line the ticket will print ("Bay 15 (VIP) · Row 7 · Seat A9 · Gate 5"),
   * keyed by seat uid. A cart item only carries the seat uid, which means nothing to a buyer.
   */
  seatDescriptions = new Map<string, string>();

  private readonly _profileProvider = inject(CHECKOUT_PROFILE_PROVIDER);
  private readonly _filesService = inject(FilesService);
  private readonly _authStateService = inject(AuthStateService);

  constructor(
    private readonly _apiService: ApiService,
    private readonly _sessionService: SessionService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _modal: NzModalService,
    private readonly _notificationService: NzNotificationService,
    private readonly _fb: FormBuilder,
    private readonly _router: Router,
    private readonly _cartStateService: CartStateService
  ) {
    this.checkoutForm = this._fb.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', [Validators.required]],
      contactNumber: ['', [Validators.required]],
      delivery: [false],
      deliveryLocationUid: [''],
      address: this._fb.group({
        addressLine1: [''],
        addressLine2: [''],
        addressLine3: [''],
        addressLine4: [''],
        state: [''],
        postalCode: [''],
        country: [''],
        countryCode: [''],
      }),
    });

    effect(() => {
      this._profileProvider.profile();
      this.populateDetails();
    });
  }

  ngOnInit(): void {
    this.getCartItems();
    this.populateDetails();
    this.scrollToTop();

    // React to the delivery toggle: load locations + apply validators when
    // enabled, clear them when disabled. Non-delivery checkout is unaffected.
    this.checkoutForm.controls['delivery'].valueChanges.subscribe(
      (enabled: boolean) => this.onDeliveryToggle(!!enabled)
    );
  }

  public scrollToTop(): void {
    this._apiService.scrollToTop();
  }

  formatCamelCase(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  }

  public purchaseDetailKeys(item: CartItem): string[] {
    const hidden = new Set([
      'seatUid',
      'selectedAddOns',
      'addOnsText',
      'addOnsTotalAmount',
      'addOnsCurrencyCode',
    ]);
    return Object.keys(item.purchaseDetails ?? {}).filter(
      (key) => !hidden.has(key)
    );
  }

  /** The seat a cart item holds, or '' for an ordinary ticket. */
  public seatUidOf(item: CartItem): string {
    return String(item.purchaseDetails?.['seatUid'] ?? '');
  }

  public seatDescription(item: CartItem): string {
    return this.seatDescriptions.get(this.seatUidOf(item)) ?? 'Reserved seat';
  }

  private loadSeatDescriptions(cartUid: string): void {
    if (!this.cartItems.some((i) => this.seatUidOf(i))) return;
    this._apiService.getSeatHolds(cartUid).subscribe({
      next: (holds) => {
        for (const hold of holds ?? []) this.seatDescriptions.set(hold.seatUid, hold.description);
      },
      error: () => undefined, // the cart still works: it says "Reserved seat"
    });
  }

  public addOnsText(item: CartItem): string {
    return String(item.purchaseDetails?.['addOnsText'] ?? '');
  }

  public hasSelectedAddOns(item: CartItem): boolean {
    return !!this.addOnsText(item);
  }

  public getCartItems(): void {
    this.loadingCart = true;

    const uuid: string = this._activatedRoute.snapshot.params['uuid'];
    this._apiService.getCartItemsByUuid(uuid).subscribe({
      next: (res: HttpResponse<CartItem[]>) => {
        this.cartItems = res.body ?? [];
        this._cartStateService.syncCartItemCount(uuid, this.cartItems);
        this.populateDetails();
        this.loadCartItemImages();
        this.loadSeatDescriptions(uuid);
        this.resolveCartCurrency();
        this.loadingCart = false;
      },

      error: () => {
        this.loadingCart = false;
      },
      complete: () => {
        this.loadingCart = false;
      },
    });
  }

  /**
   * Returns the resolved image URL for a cart item, or the placeholder
   * while it loads / on failure.
   */
  public getItemImage(item: CartItem): string {
    return this.imageUrls.get(item.parentUid) ?? 'assets/ticket.png';
  }

  /**
   * Fetch the parent event for each unique parentUid in the cart, then
   * resolve its imageFileName via FilesService (cached). Items that share
   * a parent event reuse the same cache entry.
   */
  private loadCartItemImages(): void {
    const seen = new Set<string>();
    for (const item of this.cartItems) {
      if (!item.parentUid || seen.has(item.parentUid)) continue;
      seen.add(item.parentUid);

      this._apiService.getEventByUid(item.parentUid).subscribe({
        next: (res: HttpResponse<EventModel>) => {
          // The cart is single-event, so this flag gates the delivery UI.
          if (res.body?.delivery === true) {
            this.deliveryAvailable = true;
          }
          const fileName = res.body?.imageFileName;
          if (!fileName) return;
          this._filesService.getFileDownloadUrl(fileName).subscribe({
            next: (url) => this.imageUrls.set(item.parentUid, url),
            error: () => undefined,
          });
        },
        error: () => undefined,
      });
    }
  }

  /** Resolve the cart's currency so delivery locations can be currency-matched. */
  private resolveCartCurrency(): void {
    this._cartStateService.resolveCart().subscribe({
      next: (cart) => {
        this.cartCurrency = cart?.currencyCode ?? '';
      },
      error: () => undefined,
    });
  }

  /** Convenience accessors for the template. */
  get addressGroup(): FormGroup {
    return this.checkoutForm.controls['address'] as FormGroup;
  }

  get deliveryEnabled(): boolean {
    return !!this.checkoutForm.controls['delivery'].value;
  }

  get deliveryFee(): number {
    return this.selectedDeliveryLocation?.deliveryAmount ?? 0;
  }

  get itemsTotal(): number {
    return this.cartItems.reduce((sum, item) => sum + (item.amount ?? 0), 0);
  }

  get orderTotal(): number {
    return this.itemsTotal + (this.deliveryEnabled ? this.deliveryFee : 0);
  }

  /**
   * Toggle handler: when delivery is enabled, load eligible locations and
   * require a location + street address; when disabled, reset and clear.
   */
  private onDeliveryToggle(enabled: boolean): void {
    if (enabled) {
      this.applyDeliveryValidators(true);
      this.loadDeliveryLocations();
    } else {
      this.deliveryUnavailable = false;
      this.deliveryLocations = [];
      this.selectedDeliveryLocation = null;
      this.checkoutForm.controls['deliveryLocationUid'].setValue('', {
        emitEvent: false,
      });
      this.applyDeliveryValidators(false);
    }
  }

  /** Apply/clear required validators on the location + addressLine1 controls. */
  private applyDeliveryValidators(required: boolean): void {
    const locationCtrl = this.checkoutForm.controls['deliveryLocationUid'];
    const line1Ctrl = this.addressGroup.controls['addressLine1'];
    if (required) {
      locationCtrl.setValidators([Validators.required]);
      line1Ctrl.setValidators([Validators.required]);
    } else {
      locationCtrl.clearValidators();
      line1Ctrl.clearValidators();
    }
    locationCtrl.updateValueAndValidity({ emitEvent: false });
    line1Ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /** Fetch delivery locations and keep only ACTIVE, currency-matched ones. */
  private loadDeliveryLocations(): void {
    this.loadingDeliveryLocations = true;
    this.deliveryUnavailable = false;
    this._apiService.getDeliveryLocations().subscribe({
      next: (res: HttpResponse<DeliveryLocation[]>) => {
        const all = res.body ?? [];
        this.deliveryLocations = all.filter(
          (loc) =>
            loc.status === 'ACTIVE' &&
            (!this.cartCurrency || loc.currency === this.cartCurrency)
        );
        this.deliveryUnavailable = this.deliveryLocations.length === 0;
        this.loadingDeliveryLocations = false;
      },
      error: () => {
        this.deliveryLocations = [];
        this.deliveryUnavailable = true;
        this.loadingDeliveryLocations = false;
      },
    });
  }

  /** Remember the selected location so its fee can be shown in the summary. */
  public onDeliveryLocationChange(uid: string): void {
    this.selectedDeliveryLocation =
      this.deliveryLocations.find((loc) => loc.uid === uid) ?? null;
  }

  public getOrderFees(uuid: string): void {
    this.loadingFees = true;

    this._apiService.getOrderFeesByOrderNumber(uuid).subscribe({
      next: (res: HttpResponse<OrderFee[]>) => {
        if (res.body) this.fees = res.body;
        this.loadingFees = false;
      },

      error: () => {
        this.loadingFees = false;
      },
      complete: () => {
        this.loadingFees = false;
      },
    });
  }
  public getOrderEvent(uuid: string): void {
    this.loadingOrderEvent = true;

    this._apiService.getOrderFeesByOrderNumber(uuid).subscribe({
      next: (res: HttpResponse<OrderFee[]>) => {
        if (res.body) this.fees = res.body;
        this.loadingOrderEvent = false;
      },

      error: () => {
        this.loadingOrderEvent = false;
      },
      complete: () => {
        this.loadingOrderEvent = false;
      },
    });
  }

  deleteItem(uid: string) {
    this._apiService.removeCartItem(uid).subscribe({
      next: () => {
        this.getCartItems();
        // Notify cart state service to update navbar badge
        this._cartStateService.notifyCartUpdate();
        this._notificationService.success('Deleted', 'Cart item deleted');
      },
    });
  }

  updateQuantity(item: CartItem, newQuantity: number): void {
    if (this.hasSelectedAddOns(item)) {
      this._notificationService.warning(
        'Quantity locked',
        'Tickets with add-ons can only be purchased one at a time.'
      );
      return;
    }

    if (newQuantity < 1 || newQuantity === item.quantity) {
      return;
    }

    this.loadingCart = true;

    // First remove the existing cart item, then add it back with the new quantity
    this._apiService
      .removeCartItem(item.uid)
      .pipe(
        switchMap(() => {
          const payload: AddToCartRequest = {
            parentUid: item.parentUid,
            productUid: item.productUid,
            purchaseDetails: item.purchaseDetails,
            quantity: newQuantity,
          };
          return this._apiService.addToCart(payload, item.cartUid);
        })
      )
      .subscribe({
        next: () => {
          this.getCartItems();
          this._cartStateService.notifyCartUpdate();
          this._notificationService.success(
            'Updated',
            'Cart item quantity updated'
          );
        },
        error: () => {
          this.loadingCart = false;
          this._notificationService.error(
            'Error',
            'Failed to update item quantity'
          );
        },
      });
  }

  showConfirmDelete(id: string): void {
    this._modal.confirm({
      nzTitle: 'Delete ticket from cart',
      nzContent: 'Are you sure you want to delete this ticket from cart',
      nzOkText: 'Yes',
      nzCancelText: 'Cancel',
      nzOnOk: () => this.deleteItem(id),
    });
  }

  public clearCart(): void {
    this._modal.confirm({
      nzTitle: 'Clear Cart',
      nzContent: 'Are you sure you want remove all items from cart?',
      nzOkText: 'Yes',
      nzCancelText: 'Cancel',
      nzOnOk: () => this.wipeCart(),
    });
  }

  public wipeCart(): void {
    const uuid: string = this._activatedRoute.snapshot.params['uuid'];
    this.loadingClearCart = true;

    this._apiService.clearCart(uuid).subscribe({
      next: () => {
        this.getCartItems();
        // Notify cart state service to update navbar badge
        this._cartStateService.notifyCartUpdate();
        this.loadingClearCart = false;

        this._router.navigateByUrl('/');
      },
      error: () => {
        this.loadingClearCart = false;
      },
      complete: () => {
        this.loadingClearCart = false;
      },
    });
  }

  public populateDetails(): void {
    const profile = this._profileProvider.profile();
    const authenticatedEmail = this._authStateService.getUserEmail();

    let email = '';
    let name = '';
    let contactNumber: string | undefined = '';

    if (this.cartItems && this.cartItems.length > 0) {
      const details = this.cartItems[0].purchaseDetails ?? {};
      email = details['email'] ?? '';
      const first = details['firstName'] ?? '';
      const last = details['lastName'] ?? '';
      name = first || last ? `${first} ${last}`.trim() : '';
      contactNumber = details['phoneNumber'] ?? '';
    }

    // Fill any gaps from the customer profile (cart purchaseDetails win).
    if (profile) {
      if (!email && profile.email) {
        email = profile.email;
      }
      if (!name && profile.name) {
        name = profile.name;
      }
      if (!contactNumber && profile.phoneNumber) {
        contactNumber = profile.phoneNumber;
      }
    }

    if (!email && authenticatedEmail) {
      email = authenticatedEmail;
    }

    if (email || name || contactNumber) {
      this.checkoutForm.patchValue({ email, name, contactNumber });
    }
  }

  public checkout(): void {
    const uuid: string = this._activatedRoute.snapshot.params['uuid'];

    if (this.checkoutForm.invalid) {
      this.checkoutForm.markAllAsTouched();
      Object.values(this.checkoutForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    this.checkoutLoading = true;
    const phoneNumber = this.checkoutForm.controls['contactNumber'].value;
    const phoneValue = phoneNumber?.e164Number || phoneNumber || '';

    // Delivery path: send the flag, the selected location, and an Address
    // body whose city is taken from the location (not collected from the user).
    const deliveryEnabled = this.deliveryEnabled;
    const locationUid = deliveryEnabled
      ? this.checkoutForm.controls['deliveryLocationUid'].value
      : undefined;
    let address: Address | undefined;
    if (deliveryEnabled && this.selectedDeliveryLocation) {
      address = {
        ...this.addressGroup.value,
        city: this.selectedDeliveryLocation.city,
      };
    }

    this._apiService
      .checkout(
        this.checkoutForm.controls['email'].value,
        uuid,
        phoneValue,
        this.checkoutForm.controls['name'].value,
        deliveryEnabled,
        locationUid,
        address
      )
      .subscribe({
        next: (res: HttpResponse<Order>) => {
          console.log('Res is back:::', res);
          if (res.body) {
            console.log('we are here :::', res);
            this._sessionService.saveOrderDetails(res.body, this.cartItems);
            this._router.navigateByUrl('/checkout/' + res.body.orderNumber);
            this.checkoutLoading = false;
          }
        },
        error: () => {
          this.checkoutLoading = false;
        },
        complete: () => {
          this.checkoutLoading = false;
        },
      });
  }
}
